(() => {
  const initialiseCatalog = (root) => {
    if (root.dataset.catalogReady === 'true') return;

    const grid = root.querySelector('[data-catalog-grid]');
    const items = Array.from(root.querySelectorAll('[data-catalog-item]'));
    const counts = Array.from(root.querySelectorAll('[data-catalog-count]'));
    const filterSelect = root.querySelector('[data-catalog-filter-select]');
    const sort = root.querySelector('[data-catalog-sort]');
    const toolbar = root.querySelector('[data-catalog-toolbar]');
    const mobilePanel = root.querySelector('[data-catalog-mobile-panel]');
    const pagination = root.querySelector('[data-catalog-pagination]');
    const progress = root.querySelector('[data-catalog-progress]');
    const loadMoreButton = root.querySelector('[data-catalog-load-more]');
    const fixedScope = root.dataset.catalogScope || '';
    const pageSize = Number(root.dataset.catalogPageSize) || 12;

    if (!grid || !items.length) return;

    root.dataset.catalogReady = 'true';
    const normaliseKey = (value = '') =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('vi')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    const itemKey = (item) => item.dataset.catalogKey || normaliseKey(item.dataset.catalogTitle);
    const shopifyKeys = new Set(
      items.filter((item) => item.dataset.catalogSource === 'shopify').map(itemKey).filter(Boolean),
    );
    const isReplacedSample = (item) =>
      item.dataset.catalogSource !== 'shopify' && shopifyKeys.has(itemKey(item));
    const params = new URL(window.location.href).searchParams;
    const availableScopes = ['people', 'purpose'].filter((candidate) =>
      items.some((item) => (item.dataset.catalogCategory || '').split(' ').includes(candidate)),
    );
    const requestedScope = params.get('nx_group');
    let activeScope = fixedScope || (availableScopes.includes(requestedScope) ? requestedScope : '');
    const availableFilters = () => {
      const scopedItems = items.filter((item) => {
        if (isReplacedSample(item)) return false;
        if (!activeScope) return true;
        return (item.dataset.catalogCategory || '').split(' ').includes(activeScope);
      });
      const itemFilters = scopedItems.flatMap((item) =>
        (item.dataset.catalogCategory || '').split(' ').filter(Boolean),
      );
      return [...new Set(['all', ...itemFilters])];
    };
    const externalFilterLinks = Array.from(document.querySelectorAll('[data-catalog-filter-link]'));
    const categoryFilters = ['pot', 'set', 'jar', 'cup', 'accessory', 'decor'];
    const hasCategoryNavigation = externalFilterLinks.some((link) =>
      categoryFilters.includes(link.dataset.catalogFilterLink || ''),
    );
    const requestedType = params.get('nx_type');
    let activeCategory = hasCategoryNavigation && categoryFilters.includes(requestedType) ? requestedType : 'all';
    const requestedFilter = params.get(hasCategoryNavigation ? 'nx_use' : 'nx_type');
    let activeFilter = filterSelect && availableFilters().includes(requestedFilter) ? requestedFilter : 'all';
    let visibleLimit = pageSize;

    const syncControls = () => {
      if (filterSelect) filterSelect.value = activeFilter;

      externalFilterLinks.forEach((link) => {
        const linkFilter = link.dataset.catalogFilterLink || 'all';
        const selected = hasCategoryNavigation
          ? linkFilter === activeCategory
          : linkFilter === activeFilter;
        if (selected) {
          link.setAttribute('aria-current', 'page');
          const track = link.parentElement;
          if (track && track.scrollWidth > track.clientWidth) {
            track.scrollLeft = Math.max(
              0,
              link.offsetLeft - (track.clientWidth - link.clientWidth) / 2,
            );
          }
        } else link.removeAttribute('aria-current');
      });

      toolbar?.classList.toggle('nx-catalog-toolbar--sort-only', !filterSelect);
    };

    const update = ({ resetPage = false } = {}) => {
      if (resetPage) visibleLimit = pageSize;
      if (!availableFilters().includes(activeFilter)) activeFilter = 'all';
      const direction = sort?.value || 'featured';
      const ordered = [...items].sort((a, b) => {
        if (direction === 'featured') {
          return Number(a.dataset.catalogIndex) - Number(b.dataset.catalogIndex);
        }

        const comparison = (a.dataset.catalogTitle || '').localeCompare(
          b.dataset.catalogTitle || '',
          'vi',
          { sensitivity: 'base' },
        );
        return direction === 'za' ? -comparison : comparison;
      });

      const templateCategory = activeScope || (['people', 'purpose'].includes(activeFilter) ? activeFilter : '');
      const matchingShopifyItems = ordered.filter((item) => {
        if (item.dataset.catalogSource !== 'shopify' || isReplacedSample(item)) return false;
        const categories = (item.dataset.catalogCategory || '').split(' ');
        const matchesScope = !activeScope || categories.includes(activeScope);
        const matchesCategory = activeCategory === 'all' || categories.includes(activeCategory);
        const matchesFilter = activeFilter === 'all' || categories.includes(activeFilter);
        return matchesScope && matchesCategory && matchesFilter;
      });
      const shouldShowTemplate =
        matchingShopifyItems.length === 0 && ['people', 'purpose'].includes(templateCategory);

      const eligibleItems = ordered.filter((item) => {
        if (isReplacedSample(item)) return false;
        const categories = (item.dataset.catalogCategory || '').split(' ');
        const matchesScope = !activeScope || categories.includes(activeScope);
        const matchesCategory = activeCategory === 'all' || categories.includes(activeCategory);
        const matchesFilter = activeFilter === 'all' || categories.includes(activeFilter);
        const isTemplate = Boolean(item.dataset.catalogTemplate);
        const matchesTemplate =
          shouldShowTemplate && isTemplate && item.dataset.catalogTemplate === templateCategory;
        const isDedicatedTemplateView = ['people', 'purpose'].includes(templateCategory);
        const isEligibleProduct =
          !isDedicatedTemplateView || item.dataset.catalogSource === 'shopify';
        return isTemplate
          ? matchesTemplate
          : isEligibleProduct && matchesScope && matchesCategory && matchesFilter;
      });
      const eligibleSet = new Set(eligibleItems);
      let renderedItems = 0;

      ordered.forEach((item) => {
        const canRender = eligibleSet.has(item) && renderedItems < visibleLimit;
        item.hidden = !canRender;
        if (canRender) renderedItems += 1;
        grid.appendChild(item);
      });

      const total = eligibleItems.length;
      const shown = Math.min(renderedItems, total);
      counts.forEach((count) => {
        count.textContent = `${total} sản phẩm`;
      });
      if (progress) progress.textContent = `Đang xem ${shown} / ${total} sản phẩm`;
      if (pagination) pagination.hidden = total <= pageSize;
      if (loadMoreButton) loadMoreButton.hidden = shown >= total;
      syncControls();
    };

    const syncUrl = () => {
      const nextUrl = new URL(window.location.href);
      if (activeScope && !fixedScope) nextUrl.searchParams.set('nx_group', activeScope);
      else nextUrl.searchParams.delete('nx_group');
      if (hasCategoryNavigation) {
        if (activeCategory === 'all') nextUrl.searchParams.delete('nx_type');
        else nextUrl.searchParams.set('nx_type', activeCategory);
        if (activeFilter === 'all') nextUrl.searchParams.delete('nx_use');
        else nextUrl.searchParams.set('nx_use', activeFilter);
      } else {
        nextUrl.searchParams.delete('nx_use');
        if (activeFilter === 'all') nextUrl.searchParams.delete('nx_type');
        else nextUrl.searchParams.set('nx_type', activeFilter);
      }
      nextUrl.hash = root.id || 'nx-catalog-products';
      window.history.replaceState({}, '', nextUrl);
    };

    const revealCatalog = () => {
      root.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    };

    filterSelect?.addEventListener('change', () => {
      activeFilter = filterSelect.value || 'all';
      update({ resetPage: true });
      syncUrl();
    });

    externalFilterLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const nextFilter = link.dataset.catalogFilterLink || 'all';
        activeScope = fixedScope;
        if (hasCategoryNavigation) {
          if (nextFilter !== 'all' && !categoryFilters.includes(nextFilter)) return;
        } else if (!availableFilters().includes(nextFilter)) return;

        event.preventDefault();
        if (hasCategoryNavigation) activeCategory = nextFilter;
        else activeFilter = nextFilter;
        update({ resetPage: true });
        syncUrl();
        revealCatalog();
      });
    });

    sort?.addEventListener('change', () => update({ resetPage: true }));
    if (mobilePanel) {
      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const syncMobilePanel = () => {
        if (mobileQuery.matches) {
          if (mobilePanel.dataset.mobileReady !== 'true') mobilePanel.open = false;
          mobilePanel.dataset.mobileReady = 'true';
        } else {
          mobilePanel.open = true;
          delete mobilePanel.dataset.mobileReady;
        }
      };
      syncMobilePanel();
      mobileQuery.addEventListener('change', syncMobilePanel);
    }
    loadMoreButton?.addEventListener('click', () => {
      visibleLimit += pageSize;
      update();
    });
    update();
  };

  const initialiseAll = () => {
    document.querySelectorAll('[data-catalog-root]').forEach(initialiseCatalog);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }
})();
